import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig<T> {
  key: keyof T | null;
  direction: SortDirection;
}

export interface UseSortableDataOptions<T> {
  initialSortKey?: keyof T;
  initialSortDirection?: SortDirection;
  persistKey?: string; // Key for localStorage persistence
}

/**
 * Custom hook for managing table sorting with three-state cycle:
 * null -> asc -> desc -> null
 */
export function useTableSort<T>(data: T[], options: UseSortableDataOptions<T> = {}) {
  const { initialSortKey, initialSortDirection, persistKey } = options;

  // Load initial state from localStorage if persistKey is provided
  const getInitialSortConfig = (): SortConfig<T> => {
    if (persistKey) {
      try {
        const stored = localStorage.getItem(`sort-${persistKey}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          return {
            key: parsed.key,
            direction: parsed.direction,
          };
        }
      } catch (error) {
        console.error('Failed to load sort config from localStorage:', error);
      }
    }
    return {
      key: initialSortKey || null,
      direction: initialSortDirection || null,
    };
  };

  const [sortConfig, setSortConfig] = useState<SortConfig<T>>(getInitialSortConfig);

  // Save to localStorage when sort config changes
  const updateSortConfig = useCallback(
    (newConfig: SortConfig<T>) => {
      setSortConfig(newConfig);
      if (persistKey) {
        try {
          localStorage.setItem(
            `sort-${persistKey}`,
            JSON.stringify({
              key: newConfig.key,
              direction: newConfig.direction,
            }),
          );
        } catch (error) {
          console.error('Failed to save sort config to localStorage:', error);
        }
      }
    },
    [persistKey],
  );

  /**
   * Request sort on a column - cycles through: null -> asc -> desc -> null
   */
  const requestSort = useCallback(
    (key: keyof T) => {
      let direction: SortDirection = 'asc';

      if (sortConfig.key === key) {
        if (sortConfig.direction === 'asc') {
          direction = 'desc';
        } else if (sortConfig.direction === 'desc') {
          direction = null;
        }
      }

      updateSortConfig({
        key: direction === null ? null : key,
        direction,
      });
    },
    [sortConfig, updateSortConfig],
  );

  /**
   * Sort the data based on current sort configuration
   */
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data;
    }

    const sortableData = [...data];
    const { key, direction } = sortConfig;

    sortableData.sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      // Handle null/undefined values - always put them at the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison (case-insensitive)
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return direction === 'asc' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        // Numeric comparison
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Date comparison (assumes ISO string format)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);

        // Check if valid dates
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return direction === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        }
      }

      // Fallback to string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return direction === 'asc' ? comparison : -comparison;
    });

    return sortableData;
  }, [data, sortConfig]);

  /**
   * Get the sort direction for a specific column
   */
  const getSortDirection = useCallback(
    (key: keyof T): SortDirection => {
      return sortConfig.key === key ? sortConfig.direction : null;
    },
    [sortConfig],
  );

  /**
   * Reset sort to initial state
   */
  const resetSort = useCallback(() => {
    updateSortConfig({
      key: initialSortKey || null,
      direction: initialSortDirection || null,
    });
  }, [initialSortKey, initialSortDirection, updateSortConfig]);

  return {
    sortedData,
    sortConfig,
    requestSort,
    getSortDirection,
    resetSort,
  };
}
